import { Property, PROPERTY_TYPE } from "components/pivotGrid/types"
import { groupAverangeFn, groupSumFn, sortAscFn } from "components/pivotGrid/utils/functions"
import theme from "styles/theme"
import { Colors } from "utils/color"
import { BI_PRESETS } from "../types"
import farmSo from "stores/farm"
import dayjs from "dayjs"


export function getPresetLibrary(preset: BI_PRESETS): { rowLibrary: Property[], colLibrary: Property[], valLibrary: Property[] } {
	return presets[preset ?? BI_PRESETS.YIELD]
}

const presets = {
	[BI_PRESETS.YIELD]: {
		rowLibrary: <Property[]>[

			{
				name: "growUnitId",
				label: "GROW UNIT NAME",
				description: "NAME OF THE LOCAL GROW UNIT",
				startCollapsed: false,
				render: (id: number) => farmSo.getGrowUnit(id)?.name ?? id,
				visible: false,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "growUnitId",
				label: "GROW UNIT CODE",
				description: "FULL GROW UNIT IDENTIFIER",
				startCollapsed: false,
				render: (id: number) => farmSo.getGrowUnit(id)?.growUnitCode ?? id,
				visible: false,
				onlyForCSV: true,
				type: PROPERTY_TYPE.STRING
			},

			{
				name: "cropName",
				label: "CROP",
				description: "NAME OF THE CROPS. THEY HAVE MULTIPLE CYCLES",
				startCollapsed: false,
				/*style: {minWidth: 120} */
				visible: true,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "cycleUuid",
				label: "CYCLE REF",
				description: "UNIQUE ID OF A CYCLE",
				startCollapsed: false,
				visible: false,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "lotCode",
				label: "LOT",
				description: "UNIQUE ID OF A CROP LOT",
				visible: true,
				type: PROPERTY_TYPE.STRING
			},




		],
		colLibrary: <Property[]>[			{
				name: "harvestingDate",
				label: "HARVEST DAY",
				description: "DATE OF CYCLE HARVEST",
				sort: sortAscFn,
				render: (date: string) => {
					//return date
					return dayjs(date).add(1, 'day').format('YYYY-MM-DD')
				},
				type: PROPERTY_TYPE.DATE,
				visible: true
			},
			{
				name: "harvestingDate",
				label: "CYCLE END",
				description: "LAST DAY OF LAYERS OCCUPATION",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},

			{
				name: "seedingDate",
				label: "CYCLE START",
				description: "FIRST DAY OF THE CYCLE",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},
			{
				name: "germinationDate",
				label: "GERMINATION START",
				description: "FIRST DAY OF GERMINATION PHASE",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},
			{
				name: "growingDate",
				label: "GROWING START",
				description: "FIRST DAY OF GROWING PHASE",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},


		],
		valLibrary: <Property[]>[
			{
				name: "plannedYieldBeforeIncidents",
				label: "EXPECTED YIELD",
				description: "INTIAL EXPECTED YIELD (KG) BASED ON CROP YIELD NOT CONSIDERING TRAYS LOSSES",
				render: (value: any) => value?.toFixed(1) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				visible: false,
				color: Colors[6],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "plannedYield",
				label: "EXPECTED YIELD (WITH LOSSES)",
				description: "EXPECTED YIELD (KG) CONSIDERING TRAYS LOSSES",
				visible: false,
				color: Colors[0],
				render: (value: any) => value?.toFixed(0) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "plannedYieldPerTray",
				label: "EXPECTED YIELD PER TRAY",
				description: "EXPECTED AVERAGE YIELD FOR EACH TRAY BASED ON THE CROP YIELD",
				render: (value: any) => value?.toFixed(2) ?? "--",
				visible: false,
				color: Colors[16],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "plannedYieldPerMqPerYear",
				label: "EXPECTED YIELD PER M² / YEAR",
				description: "FORECAST OF THE THEORETICAL YIELD (KG) PER M^2 WHEN GROWING A SQUARE METER OF THE CROP FOR 1 YEAR",
				render: (value: any) => value?.toFixed(2) ?? "--",
				visible: false,
				color: Colors[15],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yield",
				label: "REAL YIELD",
				description: "ACTUAL YIELD (KG) OF AN ENTIRE CROP LOT",
				render: (value: any) => value?.toFixed(0) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				visible: true,
				color: Colors[1],
				fnGroup: groupAverangeFn,
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yieldPerMqOnHarvestedTrolleys",
				label: "REAL YIELD PER M² (HARVEST)",
				description: "YIELD PER SQUARE METER ON HARVESTED TRAYS",
				render: (value: any) => value?.toFixed(2) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				visible: false,
				color: Colors[3],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yieldPerMqOnTotalTrolleys",
				label: "REAL YIELD PER M² (ALL)",
				description: "YIELD PER SQUARE METER ON ALL TRAYS (CONSIDER ALSO LOST TRAYS IN THE AVERAGE)",
				render: (value: any) => value?.toFixed(2) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				visible: false,
				color: Colors[2],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yieldPerTray",
				label: "YIELD PER TRAY",
				description: "AVERAGE YIELD OF HARVESTED TRAYS",
				render: (value: any) => value?.toFixed(2) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				visible: false,
				color: Colors[4],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yieldPerMqPerYearAll",
				label: "REAL YIELD PER M²/YEAR",
				description: "YIELD PER SQUARE METER ON ALL TRAYS FOR 1 YEAR",
				render: (value: any) => value?.toFixed(2) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				visible: false,
				color: Colors[6],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "yieldPerMqPerYearWithLosses",
				label: "REAL YIELD PER M²/YEAR (WITH LOSSES)",
				description: "YIELD PER SQUARE METER ON HARVESTED TRAYS FOR 1 YEAR",
				render: (value: any) => value?.toFixed(2) ?? "--",
				renderCsv: (value: any) => value?.toFixed(2) ?? "",
				fnGroup: groupAverangeFn,
				visible: false,
				color: Colors[5],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "affectedTrays",
				label: "REAL HARVESTED TRAYS",
				description: "TRAYS HARVESTED AND NOT AFFECTED BY INCIDENTS",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[17],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "lostYield",
				label: "LOST YIELD",
				description: "YIELD LOST (KG) DUE TO INCIDENTS ON TRAYS",
				render: (value: any) => value?.toFixed(2) ?? "--",
				visible: false,
				color: Colors[14],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "nOfIncidents",
				label: "N.OF INCIDENTS",
				description: "NUMBER OF ACCIDENTS FOR THE CURRENT CYCLE",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[11],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "trolleysToSeed",
				label: "PLANNED TROLLEYS TO SEED",
				description: "NUMBER OF TROLLEYS TO SEED FOR IN THE LOT",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[8],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "trolleysToHarvest",
				label: "PLANNED TROLLEYS TO HARVEST",
				description: "NUMBER OF PLANNED TROLLEYS TO HARVEST IN THE LOT",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[9],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "nOfTrays",
				label: "PLANNED TRAYS TO HARVEST",
				description: "NUMBER OF PLANNED  TRAYS TO HARVEST IN THE LOT",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[18],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "cycleLayers",
				label: "N. OF HARVESTING LAYERS",
				description: "NUMBER HARVESTING LAYERS OF A CROP. PORTIONS OF LAYERS COUNTS AS 1 FULL LAYER",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[21],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "seedPerTray",
				label: "SEEDS PER TRAY",
				description: "AVERAGE GRAMS OF SEEDS USED FOR EACH SEEDED TRAY",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[19],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "gelPerTray",
				label: "GEL PER TRAY",
				description: "AVERAGE GRAMS OF GEL USED FOR EACH SEEDED TRAY",
				render: (value: any) => value?.toFixed(2) ?? "--",
				visible: false,
				color: Colors[20],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "gelUsedG",
				label: "USED GEL PER LOT",
				description: "GRAMS OF GEL USED IN AN ENTIRE CROP LOT SOWING",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[22],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "seedUsedG",
				label: "SEEDS PER LOT",
				description: "GRAMS OF SEEDS USED IN AN ENTIRE CROP LOT SOWING",
				render: (value: any) => value?.toFixed(0) ?? "--",
				visible: false,
				color: Colors[23],
				type: PROPERTY_TYPE.NUMBER,
			},

			{
				name: "layersName",
				label: "LAYERS",
				description: "SET OF LAYER NAMES (NO VIRTUAL LAYERS) OF THE CROP LOT. IN THE EXPORT, IT SHOULD BE THREATED AS A SINGLE TEXT FIELD. IS A ARRAY OF STRING",
				renderCsv: (value: string[]) => value?.join(" ") ?? "",
				onlyForCSV: true,
				visible: false,
				type: PROPERTY_TYPE.STRING,
			},

			// {
			// 	name: "yieldPerTrolley",
			// 	label: "REAL YIELD PER TROLLEY",
			// 	description: "AVERAGE YIELD OF HARVESTED TROLLEYS",
			// 	render: (value: any) => value?.toFixed(1) ?? "--",
			// 	renderCsv: (value: any) => value?.toFixed(2) ?? "",
			// 	visible: false,
			// 	color: Colors[7],
			// 	type: PROPERTY_TYPE.NUMBER,
			// },

			// {
			// 	name: "unitsYield",
			// 	label: "UNITS YIELD",
			// 	description: "YIELD UNIT",
			// 	render: (value: any) => value?.toFixed(0) ?? "--",
			// 	visible: false,
			// 	color: Colors[10],
			// 	type: PROPERTY_TYPE.NUMBER,
			// },

		],
	},
	[BI_PRESETS.INCIDENTS]: {
		rowLibrary: <Property[]>[
			{
				name: "growUnitId",
				label: "GROW UNIT",
				description: "CULTIVATION UNIT IDENTIFIER",
				startCollapsed: false,
				render: (id: number) => farmSo.getGrowUnit(id)?.name ?? id,
				visible: false,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "cropName",
				label: "CROP",
				description: "NAME OF THE CROP",
				startCollapsed: false,
				/*style: {minWidth: 120} */
				visible: true,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "incidentId",
				label: "INCIDENT ID",
				description: "INCIDENT IDENTIFIER",
				startCollapsed: false,
				visible: true,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "incidentCode",
				label: "INCIDENT CODE",
				description: "ACCIDENT CODE",
				startCollapsed: false,
				visible: false,
				type: PROPERTY_TYPE.STRING
			},
		],
		colLibrary: <Property[]>[
			{
				name: "dateOfOccurrence",
				label: "OCCURRENCE",
				description: "DATE OF OCCURRENCE OF THE INCIDENT",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: true
			},
			{
				name: "harvestingDate",
				label: "HARVESTING",
				description: "CYCLE COLLECTION DATE",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},
		],
		valLibrary: <Property[]>[
			{
				name: "trolleysLost",
				label: "TROLLEYS LOST",
				description: "NUMBER OF LOST TROLLEYS",
				visible: true,
				color: theme.palette.primary.main,
				fnGroup: groupSumFn,
				type: PROPERTY_TYPE.NUMBER,
			},
			{
				name: "traysLost",
				label: "TRAYS LOST",
				description: "NUMBER OF TRAYS LOST",
				fnGroup: groupSumFn,
				visible: false,
				color: theme.palette.secondary.main,
				type: PROPERTY_TYPE.NUMBER,
			},
		],
	},
	[BI_PRESETS.ALERTS]: {
		rowLibrary: <Property[]>[
			{
				name: "growUnitId",
				label: "GROW UNIT",
				description: "CULTIVATION UNIT IDENTIFIER",
				startCollapsed: false,
				render: (id: number) => farmSo.getGrowUnit(id)?.name ?? id,
				visible: false,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "message",
				label: "TYPE",
				description: "ALERT TYPE",
				startCollapsed: false,
				/*style: {minWidth: 120} */
				visible: true,
				type: PROPERTY_TYPE.STRING
			},
			{
				name: "severity",
				label: "SEVERITY",
				description: "ALERT SEVERITY",
				startCollapsed: false,
				visible: true,
				type: PROPERTY_TYPE.NUMBER
			},
		],
		colLibrary: <Property[]>[
			{
				name: "firstOccurredAt",
				label: "FIRST",
				description: "DATE OF FIRST OCCURRENCE OF THE ALERT",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: true
			},
			{
				name: "lastOccurredAt",
				label: "LAST",
				description: "DATE OF LAST OCCURRENCE OF THE ALERT",
				sort: sortAscFn,
				type: PROPERTY_TYPE.DATE,
				visible: false
			},
		],
		valLibrary: <Property[]>[
			{
				name: "count",
				label: "COUNT",
				description: "NUMBER OF OCCURRENCES OF THE ALERT",
				visible: true,
				color: theme.palette.primary.main,
				fnGroup: groupSumFn,
				type: PROPERTY_TYPE.NUMBER,
			},
		],
	}
}

